import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import type { MeshResult, FeatureMeshMap } from "../engine/scriptRunner";
import { MATERIAL_PRESETS } from "./materials";

export interface ViewportHandle {
  fitToView: () => void;
  toggleGrid: () => void;
  toggleEdges: () => void;
  exportImage: (scale?: number) => Promise<Blob>;
  exportGLB: () => Promise<Blob>;
}

export interface MeshHitInfo {
  featureId: string;
  point: [number, number, number];
  normal: [number, number, number];
}

interface ViewportProps {
  mesh: MeshResult | null;
  featureMeshes?: FeatureMeshMap | null;
  onMeshSelect?: (featureId: string | null) => void;
  onMeshHit?: (hit: MeshHitInfo | null) => void;
  selectedFeatureId?: string | null;
  mateMode?: boolean;
  sectionEnabled?: boolean;
  sectionAxis?: "X" | "Y" | "Z";
  sectionPosition?: number;
  explodeScale?: number;
  explodeOffsets?: Record<string, [number, number, number]>;
  featureMaterials?: Record<string, string>;
  followCamera?: { position: [number, number, number]; target: [number, number, number] } | null;
  onCameraMove?: (position: [number, number, number], target: [number, number, number]) => void;
}

interface ThemeColors {
  bg: number;
  ambient: number;
  edge: number;
  gridCenter: number;
  gridLine: number;
}

function getThemeColors(): ThemeColors {
  const dark = typeof document === "undefined"
    ? true
    : document.documentElement.getAttribute("data-theme") !== "light";
  return dark
    ? { bg: 0x18181f, ambient: 0.55, edge: 0xc0c0d0, gridCenter: 0x2a2a36, gridLine: 0x1e1e28 }
    : { bg: 0xd8d8e0, ambient: 0.35, edge: 0x202028, gridCenter: 0xa0a0b0, gridLine: 0xc0c0c8 };
}

const SECTION_NORMALS: Record<string, THREE.Vector3> = {
  X: new THREE.Vector3(-1, 0, 0),
  Y: new THREE.Vector3(0, -1, 0),
  Z: new THREE.Vector3(0, 0, -1),
};

interface SceneCtx {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  shapeMesh: THREE.Mesh | null;
  edgeLines: THREE.LineSegments | null;
  featureMeshGroup: THREE.Group | null;
  featureEdgesGroup: THREE.Group | null;
  raycaster: THREE.Raycaster;
  mouse: THREE.Vector2;
}

/** Create a Three.js mesh + edges from a MeshResult, using a PBR material preset */
function buildMeshAndEdges(meshData: MeshResult, presetKey?: string): { mesh: THREE.Mesh; edges: THREE.LineSegments } {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(meshData.vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
  if (meshData.normals.length > 0) {
    geometry.setAttribute("normal", new THREE.BufferAttribute(meshData.normals, 3));
  } else {
    geometry.computeVertexNormals();
  }

  const preset = MATERIAL_PRESETS[presetKey ?? "default"] ?? MATERIAL_PRESETS.default;
  const material = new THREE.MeshStandardMaterial({
    color: preset.color,
    metalness: preset.metalness,
    roughness: preset.roughness,
    side: THREE.DoubleSide,
    flatShading: false,
    envMapIntensity: preset.envMapIntensity,
  });

  const mesh = new THREE.Mesh(geometry, material);

  const edgeGeo = new THREE.EdgesGeometry(geometry, 20);
  const edgeMat = new THREE.LineBasicMaterial({
    color: getThemeColors().edge,
    transparent: true,
    opacity: 0.6,
  });
  const edges = new THREE.LineSegments(edgeGeo, edgeMat);

  return { mesh, edges };
}

/** Fit camera to a bounding box */
function fitCameraToBox(ctx: SceneCtx, target: THREE.Object3D) {
  const box = new THREE.Box3().setFromObject(target);
  const center = new THREE.Vector3();
  box.getCenter(center);
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);
  if (maxDim === 0) return;
  const dist = maxDim * 2;
  ctx.controls.target.copy(center);
  ctx.camera.position.set(center.x + dist * 0.6, center.y + dist * 0.5, center.z + dist * 0.6);
  ctx.controls.update();
}

/** Dispose a mesh and its material */
function disposeMesh(mesh: THREE.Mesh | THREE.LineSegments) {
  mesh.geometry.dispose();
  const mat = mesh.material;
  if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
  else (mat as THREE.Material).dispose();
}

/** Clean up old scene objects */
function clearScene(ctx: SceneCtx) {
  if (ctx.shapeMesh) {
    ctx.scene.remove(ctx.shapeMesh);
    disposeMesh(ctx.shapeMesh);
    ctx.shapeMesh = null;
  }
  if (ctx.edgeLines) {
    ctx.scene.remove(ctx.edgeLines);
    disposeMesh(ctx.edgeLines);
    ctx.edgeLines = null;
  }
  if (ctx.featureMeshGroup) {
    ctx.featureMeshGroup.children.forEach((c) => disposeMesh(c as THREE.Mesh));
    ctx.scene.remove(ctx.featureMeshGroup);
    ctx.featureMeshGroup = null;
  }
  if (ctx.featureEdgesGroup) {
    ctx.featureEdgesGroup.children.forEach((c) => disposeMesh(c as THREE.LineSegments));
    ctx.scene.remove(ctx.featureEdgesGroup);
    ctx.featureEdgesGroup = null;
  }
}

const Viewport = forwardRef<ViewportHandle, ViewportProps>(function Viewport(
  { mesh, featureMeshes, onMeshSelect, onMeshHit, selectedFeatureId, mateMode, sectionEnabled, sectionAxis, sectionPosition, explodeScale, explodeOffsets, featureMaterials, followCamera, onCameraMove },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<THREE.GridHelper | null>(null);
  const sceneRef = useRef<SceneCtx | null>(null);
  // Store callbacks in refs so the click handler always has the latest
  const onMeshSelectRef = useRef(onMeshSelect);
  onMeshSelectRef.current = onMeshSelect;
  const onMeshHitRef = useRef(onMeshHit);
  onMeshHitRef.current = onMeshHit;
  const mateModeRef = useRef(mateMode);
  mateModeRef.current = mateMode;
  const followCameraRef = useRef(followCamera);
  followCameraRef.current = followCamera;
  const onCameraMoveRef = useRef(onCameraMove);
  onCameraMoveRef.current = onCameraMove;
  const cameraBroadcastCounter = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const colors = getThemeColors();

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(colors.bg);
    scene.fog = new THREE.FogExp2(colors.bg, 0.006);

    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 2000);
    camera.position.set(40, 30, 40);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.localClippingEnabled = true;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.8;
    controls.zoomSpeed = 1.2;
    controls.panSpeed = 0.6;
    controls.minDistance = 5;
    controls.maxDistance = 500;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, colors.ambient);
    scene.add(ambientLight);
    const keyLight = new THREE.DirectionalLight(0xf0f0ff, 0.9);
    keyLight.position.set(60, 80, 40);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xd0e0ff, 0.35);
    fillLight.position.set(-40, 20, -30);
    scene.add(fillLight);
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.15);
    rimLight.position.set(0, -20, 60);
    scene.add(rimLight);

    // Environment map for PBR reflections
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = new THREE.Scene();
    envScene.add(new THREE.HemisphereLight(0xc0d0f0, 0x303040, 1.0));
    const envDir = new THREE.DirectionalLight(0xffffff, 0.6);
    envDir.position.set(1, 1, 1);
    envScene.add(envDir);
    scene.environment = pmrem.fromScene(envScene, 0).texture;
    pmrem.dispose();

    // Grid
    const grid = new THREE.GridHelper(200, 40, colors.gridCenter, colors.gridLine);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.6;
    scene.add(grid);
    gridRef.current = grid;

    // Origin axes
    const axLen = 15;
    const axGeo = new THREE.BufferGeometry();
    axGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([
      0, 0, 0, axLen, 0, 0, 0, 0, 0, 0, axLen, 0, 0, 0, 0, 0, 0, axLen,
    ]), 3));
    axGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array([
      0.8, 0.25, 0.25, 0.8, 0.25, 0.25, 0.25, 0.7, 0.25, 0.25, 0.7, 0.25, 0.3, 0.4, 0.9, 0.3, 0.4, 0.9,
    ]), 3));
    scene.add(new THREE.LineSegments(axGeo, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.5 })));

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    sceneRef.current = {
      scene, camera, renderer, controls,
      shapeMesh: null, edgeLines: null,
      featureMeshGroup: null, featureEdgesGroup: null,
      raycaster, mouse,
    };

    let frameId: number;
    function animate() {
      frameId = requestAnimationFrame(animate);
      // Follow mode: sync camera to followed user's position
      const follow = followCameraRef.current;
      if (follow) {
        camera.position.set(...follow.position);
        controls.target.set(...follow.target);
      }
      controls.update();
      renderer.render(scene, camera);
      // Broadcast camera position every 10 frames (~3Hz)
      if (onCameraMoveRef.current && ++cameraBroadcastCounter.current >= 10) {
        cameraBroadcastCounter.current = 0;
        const p = camera.position;
        const t = controls.target;
        onCameraMoveRef.current([p.x, p.y, p.z], [t.x, t.y, t.z]);
      }
    }
    animate();

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    // Click-to-select: raycast against feature meshes or single mesh
    const onClick = (e: MouseEvent) => {
      const ctx = sceneRef.current;
      if (!ctx) return;
      const rect = renderer.domElement.getBoundingClientRect();
      ctx.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ctx.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      ctx.raycaster.setFromCamera(ctx.mouse, ctx.camera);

      const objectsToIntersect = ctx.featureMeshGroup ? ctx.featureMeshGroup.children : (ctx.shapeMesh ? [ctx.shapeMesh] : []);
      if (objectsToIntersect.length === 0) return;

      const hits = ctx.raycaster.intersectObjects(objectsToIntersect);
      if (hits.length > 0) {
        const hit = hits[0];
        const featureId = hit.object.userData.featureId as string || "__last";
        onMeshSelectRef.current?.(featureId);

        // Compute face normal from triangle vertices
        if (onMeshHitRef.current && hit.face) {
          const geo = (hit.object as THREE.Mesh).geometry;
          const pos = geo.getAttribute("position");
          const idx = geo.index;
          if (pos && idx && hit.faceIndex != null) {
            const fi = hit.faceIndex * 3;
            const a = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(fi));
            const b = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(fi + 1));
            const c = new THREE.Vector3().fromBufferAttribute(pos, idx.getX(fi + 2));

            // World-space normal calculation
            const normal = new THREE.Vector3().crossVectors(
              b.clone().sub(a), c.clone().sub(a)
            ).normalize();
            // Transform normal to world space (handles part rotation/translation)
            normal.applyQuaternion(hit.object.quaternion).normalize();

            onMeshHitRef.current({
              featureId,
              point: [hit.point.x, hit.point.y, hit.point.z],
              normal: [normal.x, normal.y, normal.z],
            });
          }
        }
      } else {
        onMeshSelectRef.current?.(null);
        onMeshHitRef.current?.(null);
      }
    };
    renderer.domElement.addEventListener("click", onClick);

    const themeObserver = new MutationObserver(() => {
      const c = getThemeColors();
      scene.background = new THREE.Color(c.bg);
      (scene.fog as THREE.FogExp2).color.set(c.bg);
      ambientLight.intensity = c.ambient;
      const gridMats = Array.isArray(grid.material) ? grid.material : [grid.material];
      gridMats.forEach((m, i) => (m as THREE.LineBasicMaterial).color.set(i === 0 ? c.gridCenter : c.gridLine));
      const ctx = sceneRef.current;
      const setEdgeColor = (obj: THREE.Object3D | null) => {
        if (!obj) return;
        obj.traverse((o) => {
          if ((o as THREE.LineSegments).isLineSegments) {
            const mat = (o as THREE.LineSegments).material as THREE.LineBasicMaterial;
            if (mat && "color" in mat) mat.color.set(c.edge);
          }
        });
      };
      setEdgeColor(ctx?.edgeLines ?? null);
      setEdgeColor(ctx?.featureEdgesGroup ?? null);
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      renderer.domElement.removeEventListener("click", onClick);
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    fitToView() {
      const ctx = sceneRef.current;
      if (!ctx) return;
      const target = ctx.featureMeshGroup ?? ctx.shapeMesh;
      if (target) fitCameraToBox(ctx, target);
    },
    toggleGrid() {
      const grid = gridRef.current;
      if (grid) grid.visible = !grid.visible;
    },
    toggleEdges() {
      const ctx = sceneRef.current;
      if (ctx?.featureEdgesGroup) ctx.featureEdgesGroup.visible = !ctx.featureEdgesGroup.visible;
      else if (ctx?.edgeLines) ctx.edgeLines.visible = !ctx.edgeLines.visible;
    },
    async exportImage(scale = 2) {
      const ctx = sceneRef.current;
      if (!ctx) throw new Error("No scene");
      const w = ctx.renderer.domElement.width * scale;
      const h = ctx.renderer.domElement.height * scale;
      const exportRenderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
      exportRenderer.setSize(w, h);
      exportRenderer.toneMapping = THREE.ACESFilmicToneMapping;
      exportRenderer.toneMappingExposure = 1.1;
      if (ctx.scene.environment) exportRenderer.outputColorSpace = THREE.SRGBColorSpace;
      exportRenderer.render(ctx.scene, ctx.camera);
      return new Promise<Blob>((resolve, reject) => {
        exportRenderer.domElement.toBlob((blob) => {
          exportRenderer.dispose();
          if (blob) resolve(blob);
          else reject(new Error("Failed to export image"));
        }, "image/png");
      });
    },
    async exportGLB() {
      const ctx = sceneRef.current;
      if (!ctx) throw new Error("No scene");
      const target = ctx.featureMeshGroup ?? ctx.shapeMesh;
      if (!target) throw new Error("No geometry to export");
      const exporter = new GLTFExporter();
      return new Promise<Blob>((resolve, reject) => {
        exporter.parse(target, (result) => {
          resolve(new Blob([result as ArrayBuffer], { type: "model/gltf-binary" }));
        }, reject, { binary: true });
      });
    },
  }));

  // Highlight selected feature mesh via emissive glow
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx?.featureMeshGroup) return;
    ctx.featureMeshGroup.children.forEach((child) => {
      const m = child as THREE.Mesh;
      const mat = m.material as THREE.MeshStandardMaterial;
      const isSelected = m.userData.featureId === selectedFeatureId;
      mat.emissive.setHex(isSelected ? 0x2244aa : 0x000000);
      mat.emissiveIntensity = isSelected ? 0.3 : 0;
    });
  }, [selectedFeatureId]);

  // Section clipping plane
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx) return;
    if (sectionEnabled) {
      const normal = SECTION_NORMALS[sectionAxis ?? "X"];
      ctx.renderer.clippingPlanes = [new THREE.Plane(normal, sectionPosition ?? 0)];
    } else {
      ctx.renderer.clippingPlanes = [];
    }
  }, [sectionEnabled, sectionAxis, sectionPosition]);

  // Exploded view — translate each mesh by its explosion offset × scale
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx?.featureMeshGroup || !explodeOffsets) return;
    const scale = explodeScale ?? 0;
    ctx.featureMeshGroup.children.forEach((child, i) => {
      const fid = child.userData.featureId as string;
      const offset = explodeOffsets[fid];
      if (offset) {
        child.position.set(offset[0] * scale, offset[1] * scale, offset[2] * scale);
      } else {
        child.position.set(0, 0, 0);
      }
      // Also move corresponding edge mesh
      if (ctx.featureEdgesGroup?.children[i]) {
        ctx.featureEdgesGroup.children[i].position.copy(child.position);
      }
    });
  }, [explodeScale, explodeOffsets]);

  // Update single mesh (legacy / script mode fallback)
  const updateMesh = useCallback((meshData: MeshResult) => {
    const ctx = sceneRef.current;
    if (!ctx) return;
    clearScene(ctx);

    const { mesh: m, edges } = buildMeshAndEdges(meshData);
    ctx.shapeMesh = m;
    ctx.edgeLines = edges;
    ctx.scene.add(m);
    ctx.scene.add(edges);
    fitCameraToBox(ctx, m);
  }, []);

  // Update per-feature meshes
  const updateFeatureMeshes = useCallback((map: FeatureMeshMap) => {
    const ctx = sceneRef.current;
    if (!ctx) return;
    clearScene(ctx);

    const meshGroup = new THREE.Group();
    const edgeGroup = new THREE.Group();

    for (const [featureId, meshData] of Object.entries(map)) {
      const { mesh: m, edges } = buildMeshAndEdges(meshData, featureMaterials?.[featureId]);
      m.userData.featureId = featureId;
      meshGroup.add(m);
      edgeGroup.add(edges);
    }

    ctx.featureMeshGroup = meshGroup;
    ctx.featureEdgesGroup = edgeGroup;
    ctx.scene.add(meshGroup);
    ctx.scene.add(edgeGroup);
    fitCameraToBox(ctx, meshGroup);
  }, []);

  // React to mesh data changes
  useEffect(() => {
    if (featureMeshes && Object.keys(featureMeshes).length > 0) {
      updateFeatureMeshes(featureMeshes);
    } else if (mesh) {
      updateMesh(mesh);
    }
  }, [mesh, featureMeshes, updateMesh, updateFeatureMeshes]);

  // Update materials on existing meshes when presets change (without geometry rebuild)
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx?.featureMeshGroup || !featureMaterials) return;
    ctx.featureMeshGroup.children.forEach((child) => {
      const m = child as THREE.Mesh;
      const mat = m.material as THREE.MeshStandardMaterial;
      const preset = MATERIAL_PRESETS[featureMaterials[m.userData.featureId] ?? "default"] ?? MATERIAL_PRESETS.default;
      mat.color.setHex(preset.color);
      mat.metalness = preset.metalness;
      mat.roughness = preset.roughness;
      mat.envMapIntensity = preset.envMapIntensity;
    });
  }, [featureMaterials]);

  return (
    <div ref={containerRef} className="viewport-canvas" />
  );
});

export default Viewport;
